import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "../api";
import type { User } from "../App";
import { useTurnstile } from "../use-turnstile";

type LoginLocationState = {
  accountDeletionStarted?: boolean;
  accountDeletionMessage?: string;
  verifyEmailSent?: boolean;
  registeredEmail?: string;
};

export default function Login({ onLogin }: { onLogin: (u: User) => void }) {
  const location = useLocation();
  const loc = location.state as LoginLocationState | null | undefined;
  const deletionNotice = loc?.accountDeletionStarted
    ? loc.accountDeletionMessage ||
      "Your account is being removed in the background. You cannot sign in until that finishes. Check your email for confirmation when it is done."
    : null;
  const verifyNotice = loc?.verifyEmailSent
    ? `We sent a verification link${loc.registeredEmail ? ` to ${loc.registeredEmail}` : ""}. Open it to confirm your email, then sign in below.`
    : null;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string | null>(null);
  const { containerRef, getToken, reset, loadingConfig, required: turnstileRequired } = useTurnstile(
    turnstileSiteKey,
    true,
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("/api/auth/config");
        const j = (await r.json()) as { turnstileSiteKey?: string };
        if (!cancelled) setTurnstileSiteKey(j.turnstileSiteKey?.trim() ? j.turnstileSiteKey : "");
      } catch {
        if (!cancelled) setTurnstileSiteKey("");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (turnstileRequired && !getToken()) {
      setError("Complete the security check before signing in.");
      return;
    }
    setBusy(true);
    const r = await api<{ user: User }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({
        email,
        password,
        ...(turnstileRequired ? { turnstileToken: getToken() } : {}),
      }),
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error || "Login failed");
      reset();
      return;
    }
    if (r.data?.user) onLogin(r.data.user);
  }

  return (
    <div className="layout">
      <header className="layout-header layout-header--auth">
        <Link to="/" className="brand" aria-label="HAB LOG home">
          <span className="brand-mark">HAB</span>
          <span className="brand-sub">LOG</span>
        </Link>
      </header>
      <main className="layout-main layout-main--narrow">
        <h1>Welcome back</h1>
        <p className="muted">Login to your private cloud neural index.</p>
        {deletionNotice ? (
          <div className="account-feedback account-feedback--success" role="status" style={{ marginBottom: "1rem" }}>
            <p>{deletionNotice}</p>
          </div>
        ) : null}
        {verifyNotice ? (
          <div className="account-feedback account-feedback--success" role="status" style={{ marginBottom: "1rem" }}>
            <p>{verifyNotice}</p>
          </div>
        ) : null}
        <form className="card" onSubmit={submit}>
          <div className="field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          {turnstileRequired ? (
            <div className="field turnstile-field">
              <span className="turnstile-label">Verification</span>
              <div ref={containerRef} className="turnstile-container" />
            </div>
          ) : null}
          {error ? <p className="error">{error}</p> : null}
          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: "100%" }}
            disabled={busy || loadingConfig}
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
        <p className="muted" style={{ marginTop: "1rem" }}>
          <Link to="/forgot-password">Forgot password?</Link>
        </p>
        <p className="muted" style={{ marginTop: "0.5rem" }}>
          No account? <Link to="/register">Create one</Link>
        </p>
        <p className="legal-auth-links muted">
          <Link to="/terms">Terms</Link>
          {" · "}
          <Link to="/privacy">Privacy</Link>
        </p>
      </main>
    </div>
  );
}
